// Platform Setup Modal - Improved gaming platform setup flow
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  X, ChevronRight, ChevronLeft, Check,
  ExternalLink,
  Loader2
} from '../../common/Icons';
import CountrySelector from '../../common/CountrySelector';

// Platform icons
const SteamIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10c0-5.52-4.48-10-10-10zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
  </svg>
);

const PlayStationIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
  </svg>
);

const XboxIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
  </svg>
);

const STEPS = {
  COUNTRY: 1,
  POSITION: 2,
  PLATFORM: 3,
  AUTH: 4,
  COMPLETE: 5
};

const PLATFORMS = [
  { 
    id: 'steam', 
    name: 'Steam', 
    icon: SteamIcon,
    authType: 'oauth',
    description: 'Connect with Steam OpenID'
  },
  { 
    id: 'psn', 
    name: 'PlayStation', 
    icon: PlayStationIcon,
    authType: 'manual',
    description: 'Enter PSN username'
  },
  { 
    id: 'xbox', 
    name: 'Xbox Live', 
    icon: XboxIcon,
    authType: 'oauth',
    description: 'Connect with Xbox Live'
  }
];

const POSITIONS = [
  { id: 'goalkeeper', name: 'Goalkeeper', icon: '/glove.ico' },
  { id: 'defender', name: 'Defender', icon: '/shield.ico' },
  { id: 'midfielder', name: 'Midfielder', icon: '/pulm.ico' },
  { id: 'forward', name: 'Forward', icon: '/tiger.ico' }
];

const PlatformSetupModal = ({ onClose, onComplete }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(STEPS.COUNTRY);
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [skipVerification, setSkipVerification] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    platform: null,
    platformId: '',
    country_code: '',
    position: null,
    jersey_number: ''
  });

  // Animate in on mount
  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  const checkSteamProfile = useCallback(async () => {
    const { data: profile } = await supabase
      .from('users')
      .select('steam_profile_id, gamer_tag')
      .eq('id', user.id)
      .single();

    if (profile?.steam_profile_id) {
      setFormData(prev => ({
        ...prev,
        platform: 'steam',
        platformId: profile.gamer_tag || profile.steam_profile_id
      }));
      setCurrentStep(STEPS.COMPLETE);
      toast.success('Steam account connected successfully!');
    }
  }, [user?.id]);

  const checkXboxProfile = useCallback(async () => {
    const { data: profile } = await supabase
      .from('users')
      .select('gamer_tag, xbox_id')
      .eq('id', user.id)
      .single();

    if (profile?.gamer_tag) {
      setFormData(prev => ({
        ...prev,
        platform: 'xbox',
        platformId: profile.gamer_tag
      }));
      setCurrentStep(STEPS.COMPLETE);
      toast.success('Xbox account connected successfully!');
    }
  }, [user?.id]);

  // Check for OAuth callback parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Check for Steam auth callback
    if (params.get('steam_auth') === 'success') {
      checkSteamProfile();
    } else if (params.get('steam_auth') === 'error') {
      toast.error('Steam authentication failed: ' + (params.get('error_message') || 'Unknown error'));
    }

    // Check for Xbox auth callback
    if (params.get('xbox_auth') === 'success') {
      checkXboxProfile();
    } else if (params.get('xbox_auth') === 'error') {
      toast.error('Xbox authentication failed: ' + (params.get('error_message') || 'Unknown error'));
    }
  }, [location, checkSteamProfile, checkXboxProfile]);

  const handlePlatformSelect = (platform) => {
    setFormData({ ...formData, platform: platform.id });
    
    // For OAuth platforms, go directly to auth
    if (platform.authType === 'oauth') {
      handleOAuth(platform.id);
    } else {
      setCurrentStep(STEPS.AUTH);
    }
  };

  const handleOAuth = async (platform) => {
    setLoading(true);
    try {
      console.log('Starting OAuth for platform:', platform);
      console.log('User ID:', user?.id);
      console.log('User object:', user);
      
      // Save country, jersey number and position before OAuth redirect to prevent data loss
      if (formData.country_code && formData.position) {
        console.log('Saving country, jersey number and position before OAuth redirect:', { 
          country_code: formData.country_code, 
          position: formData.position,
          jersey_number: formData.jersey_number 
        });
        
        const { error: updateError } = await supabase
          .from('users')
          .update({
            country_code: formData.country_code,
            position: formData.position,
            jersey_number: parseInt(formData.jersey_number)
          })
          .eq('id', user.id);
          
        if (updateError) {
          console.error('Error saving country/position:', updateError);
          throw updateError;
        }
      }
      
      if (platform === 'steam') {
        // Get Steam auth URL
        const { data, error } = await supabase.functions.invoke('steam-auth', {
          body: { userId: user.id }
        });
        
        if (error) throw error;
        
        if (data?.authUrl) {
          // Redirect to Steam OAuth
          window.location.href = data.authUrl;
        } else {
          throw new Error('Failed to get Steam auth URL');
        }
      } else if (platform === 'xbox') {
        // Get Xbox auth URL
        const { data, error } = await supabase.functions.invoke('xbox-auth', {
          body: { userId: user.id }
        });
        
        if (error) throw error;
        
        if (data?.authUrl) {
          // Redirect to Xbox OAuth
          window.location.href = data.authUrl;
        } else {
          throw new Error('Failed to get Xbox auth URL');
        }
      }
    } catch (error) {
      console.error('OAuth error:', error);
      toast.error(`Failed to connect ${platform === 'steam' ? 'Steam' : 'Xbox'} account`);
      setLoading(false);
    }
  };

  const validatePlatformInput = (platform, input) => {
    if (!input || input.trim().length === 0) {
      return { success: false, error: 'Please enter your username' };
    }
    
    const trimmed = input.trim();
    
    if (platform === 'psn') {
      if (trimmed.length < 3 || trimmed.length > 16) {
        return { success: false, error: 'PSN username must be between 3-16 characters' };
      }
    } else if (platform === 'xbox') {
      if (trimmed.length < 1 || trimmed.length > 15) {
        return { success: false, error: 'Xbox Gamertag must be between 1-15 characters' };
      }
    }
    
    return { success: true };
  };

  const handleManualSubmit = async () => {
    if (!formData.platformId.trim()) {
      toast.error(`Please enter your ${formData.platform === 'psn' ? 'PSN username' : 'Xbox Gamertag'}`);
      return;
    }
    
    // Validate input
    const validation = validatePlatformInput(formData.platform, formData.platformId);
    if (!validation.success) {
      toast.error(validation.error);
      return;
    }
    
    setLoading(true);
    
    // Try to verify with tracker API unless skipped
    if (!skipVerification) {
      try {
        const { data } = await supabase.functions.invoke('verify-player-profile', {
          body: { 
            platform: formData.platform,
            identifier: formData.platformId 
          }
        });
        
        if (data?.verified && data?.playerData) {
          // Show verification screen
          setVerificationData(data.playerData);
          setShowVerification(true);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.warn('Verification failed, continuing without verification:', error);
        // Continue without verification
      }
    }
    
    // Continue without verification
    handleComplete();
    toast.success(`${formData.platform === 'psn' ? 'PlayStation' : 'Xbox'} account saved!`);
    setLoading(false);
  };

  const handleVerificationConfirm = async () => {
    if (verificationData?.displayName) {
      try {
        // Update the user's gamer_tag with the verified display name
        const { error } = await supabase
          .from('users')
          .update({ 
            gamer_tag: verificationData.displayName,
            platform_verified: true
          })
          .eq('id', user.id);
          
        if (error) {
          console.error('Error updating display name:', error);
        } else {
          // Update form data with verified display name
          setFormData(prev => ({ 
            ...prev, 
            platformId: verificationData.displayName 
          }));
        }
      } catch (error) {
        console.error('Error saving verified display name:', error);
      }
    }
    
    setShowVerification(false);
    setVerificationData(null);
    handleComplete();
    toast.success('Account verified successfully!');
  };

  const handleVerificationReject = () => {
    setShowVerification(false);
    setVerificationData(null);
    setFormData({ ...formData, platformId: '' });
  };

  const handleCountrySubmit = () => {
    if (!formData.country_code) {
      toast.error('Please select your country');
      return;
    }
    if (!formData.jersey_number || formData.jersey_number < 0 || formData.jersey_number > 99) {
      toast.error('Please enter a valid jersey number (0-99)');
      return;
    }
    setCurrentStep(STEPS.POSITION);
  };

  const handlePositionSelect = (position) => {
    setFormData({ ...formData, position: position.id });
    setCurrentStep(STEPS.PLATFORM);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Update user profile
      const updateData = {
        platform: formData.platform,
        country_code: formData.country_code,
        position: formData.position,
        jersey_number: parseInt(formData.jersey_number),
        platform_verified: !!verificationData
      };
      
      // Set platform-specific data
      if (formData.platform === 'steam') {
        // For Steam, we should already have steam_profile_id from OAuth
        // and gamer_tag should contain the display name
        updateData.gamer_tag = formData.platformId; // This should be the display name
      } else if (formData.platform === 'xbox') {
        // For Xbox, both xbox_id and gamer_tag should be set from OAuth
        updateData.gamer_tag = formData.platformId; // This should be the gamertag
      } else {
        // For PSN, just set gamer_tag
        updateData.gamer_tag = formData.platformId;
      }
      
      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) throw error;
      
      setCurrentStep(STEPS.COMPLETE);
      toast.success('Profile setup complete!');
      
      // Open Discord in new tab
      setTimeout(() => {
        window.open('https://discord.gg/virtualrematchgaming', '_blank');
        if (onComplete) onComplete();
        if (onClose) onClose();
      }, 2000);
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    }
    setLoading(false);
  };

  const renderStepContent = () => {
    // Verification Screen
    if (showVerification && verificationData) {
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-white mb-2">Is this you?</h2>
          <p className="text-zinc-400 mb-6">We found a profile matching your username</p>
          
          <div className="bg-[#22262f] border border-white/[0.06] rounded-lg p-4">
            <div className="flex items-center gap-4">
              {verificationData.avatar && (
                <img 
                  src={verificationData.avatar} 
                  alt="Profile" 
                  className="w-16 h-16 rounded-full"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div>
                <p className="text-white font-semibold text-lg">
                  {verificationData.displayName || formData.platformId}
                </p>
                {verificationData.stats && (
                  <div className="text-sm text-zinc-400 mt-1">
                    {verificationData.stats.rank && (
                      <p>Rank: {verificationData.stats.rank}</p>
                    )}
                    {verificationData.stats.wins !== undefined && (
                      <p>Wins: {verificationData.stats.wins}</p>
                    )}
                    {verificationData.stats.goals !== undefined && (
                      <p>Goals: {verificationData.stats.goals}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleVerificationConfirm}
              className="flex-1 px-6 py-3 bg-[#31C590] hover:bg-[#23F8B5] text-zinc-900 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Yes, that's me
            </button>

            <button
              onClick={handleVerificationReject}
              className="flex-1 px-6 py-3 bg-[#22262f] border border-white/10 hover:bg-[#2a2f3a] text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              No, try again
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case STEPS.COUNTRY:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Select Your Country</h2>
            <p className="text-zinc-400 mb-6">This helps us match you with nearby players</p>
            
            <CountrySelector
              value={formData.country_code}
              onChange={(value) => setFormData({ ...formData, country_code: value })}
              placeholder="Choose a country..."
              className="w-full"
            />
            
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Jersey Number</label>
              <input
                type="number"
                min="0"
                max="99"
                value={formData.jersey_number}
                onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                placeholder="Enter your jersey number (0-99)"
                className="w-full px-4 py-3 bg-[#22262f] border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-[#31C590]/50"
              />
            </div>
            
            <button
              onClick={handleCountrySubmit}
              disabled={!formData.country_code || !formData.jersey_number}
              className="w-full px-6 py-3 bg-[#31C590] hover:bg-[#23F8B5] disabled:bg-[#22262f] disabled:text-zinc-500 text-zinc-900 rounded-lg font-semibold transition-colors"
            >
              Continue
            </button>
          </div>
        );
        
      case STEPS.AUTH:
        const selectedPlatform = PLATFORMS.find(p => p.id === formData.platform);
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">
              Connect {selectedPlatform?.name}
            </h2>
            
            {formData.platform === 'steam' && (
              <div className="text-center py-8">
                <p className="text-zinc-400 mb-6">
                  Click below to sign in with your Steam account
                </p>
                <button
                  onClick={() => handleOAuth('steam')}
                  disabled={loading}
                  className="px-8 py-4 bg-[#22262f] border border-white/10 hover:border-[#31C590]/40 hover:bg-[#262b35] disabled:opacity-60 text-white rounded-lg font-semibold transition-colors flex items-center gap-3 mx-auto"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      <SteamIcon />
                      <span>Sign in with Steam</span>
                      <ExternalLink className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            
            {(formData.platform === 'psn' || formData.platform === 'xbox') && (
              <div className="space-y-4">
                <p className="text-zinc-400">
                  Enter your {formData.platform === 'psn' ? 'PlayStation Network username' : 'Xbox Live Gamertag'}
                </p>
                
                <input
                  type="text"
                  value={formData.platformId}
                  onChange={(e) => setFormData({ ...formData, platformId: e.target.value })}
                  placeholder={formData.platform === 'psn' ? 'PSN username' : 'Xbox Gamertag'}
                  className="w-full px-4 py-3 bg-[#22262f] border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-[#31C590]/50"
                  maxLength={formData.platform === 'psn' ? 16 : 15}
                />
                
                <div className="text-xs text-zinc-400">
                  {formData.platform === 'psn' ? 
                    'Your PlayStation Network username (3-16 characters)' : 
                    'Your Xbox Live Gamertag (1-15 characters)'}
                </div>
                
                <button
                  onClick={handleManualSubmit}
                  disabled={loading || !formData.platformId.trim()}
                  className="w-full px-6 py-3 bg-[#31C590] hover:bg-[#23F8B5] disabled:bg-[#22262f] disabled:text-zinc-500 text-zinc-900 rounded-lg font-semibold transition-colors"
                >
                  {loading ? (
                    <Loader2 className="animate-spin mx-auto" />
                  ) : (
                    'Continue'
                  )}
                </button>
                
                {/* Skip verification option */}
                <button
                  onClick={() => {
                    setSkipVerification(true);
                    handleManualSubmit();
                  }}
                  disabled={loading || !formData.platformId.trim()}
                  className="w-full text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Skip verification (if service is unavailable)
                </button>
              </div>
            )}
          </div>
        );
        
      case STEPS.POSITION:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Your Position</h2>
            <p className="text-zinc-400 mb-6">What's your preferred playing style?</p>
            
            <div className="grid grid-cols-1 gap-3">
              {POSITIONS.map(position => (
                <button
                  key={position.id}
                  onClick={() => handlePositionSelect(position)}
                  className="p-4 bg-[#22262f] border border-white/10 hover:border-[#31C590]/40 hover:bg-[#262b35] rounded-lg text-white transition-colors flex items-center gap-4"
                >
                  <img src={position.icon} alt={position.name} className="w-8 h-8 object-contain" />
                  <span className="text-lg font-medium">{position.name}</span>
                </button>
              ))}
            </div>
          </div>
        );
        
      case STEPS.PLATFORM:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Your Platform</h2>
            <p className="text-zinc-400 mb-6">Select your primary gaming platform</p>
            
            <div className="grid grid-cols-1 gap-4">
              {PLATFORMS.map(platform => {
                const Icon = platform.icon;
                return (
                  <button
                    key={platform.id}
                    onClick={() => handlePlatformSelect(platform)}
                    className="p-5 rounded-xl bg-[#22262f] border border-white/10 hover:border-[#31C590]/40 hover:bg-[#262b35] transition-colors text-white"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-[#23F8B5]"><Icon /></span>
                        <div className="text-left">
                          <div className="text-xl font-bold">{platform.name}</div>
                          <div className="text-sm text-zinc-400">{platform.description}</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case STEPS.COMPLETE:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-[#31C590] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-zinc-900" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Setup Complete!</h2>
            <p className="text-zinc-400 mb-6">Redirecting you to Discord...</p>
            <Loader2 className="animate-spin mx-auto text-[#23F8B5]" />
          </div>
        );
        
      default:
        return null;
    }
  };

  return createPortal(
    <div className={`fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div
        className={`relative bg-[#1e2129] border border-white/10 rounded-2xl shadow-2xl max-w-md w-full my-8 max-h-[90vh] overflow-y-auto transition-transform duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#1e2129] p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Setup Your Gaming Profile</h1>
            {currentStep !== STEPS.COMPLETE && (
              <button
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(onClose, 300);
                }}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 mb-2">
              {[1, 2, 3, 4].map(step => (
                <div
                  key={step}
                  className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                    step <= currentStep ? 'bg-[#31C590]' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-zinc-400 text-center font-medium">
              Step {Math.min(currentStep, 4)} of 4
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        {currentStep > STEPS.COUNTRY && currentStep < STEPS.COMPLETE && !showVerification && (
          <div className="px-6 pb-6">
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors font-medium"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PlatformSetupModal;